/* eslint-disable @typescript-eslint/no-explicit-any */
import SummaryCard from 'components/SummaryCard';
import { Badge } from 'reactstrap';

interface WebAppInfoSummaryProps {
  data: any;
  description?: React.ReactNode;
  mode?: 'normal' | 'tooltip';
}

const WebAppInfoSummary = ({
  data,
  description,
  mode = 'normal',
}: WebAppInfoSummaryProps) => {
  // Extract relevant data from props
  const {
    name,
    url,
    webapp_id,
    webapp_os,
    is_active,
    webapp_owner,
    webapp_authRecords,
    last_scan,
  } = data || {};

  // Determine authentication status
  const authRecords = webapp_authRecords;

  // Get auth status from last scan or current data
  const scanData = last_scan;
  const authStatus = scanData?.summary?.authStatus;

  const hasAuthRecords =
    authRecords?.list?.length > 0 || String(authStatus).length > 0;

  // Format owner name if available
  const ownerName = webapp_owner
    ? `${webapp_owner.fullname || webapp_owner.username || 'N/A'}`
    : 'N/A';

  // Determine authentication badge color and text
  const getAuthBadge = () => {
    if (!hasAuthRecords)
      return <Badge color="secondary">No Auth Records</Badge>;

    // Status mapping for labels and colors
    const authStatusMap: Record<string, { label: string; color: string }> = {
      SUCCESSFUL: { label: 'Auth Success', color: 'success' },
      FAILED: { label: 'Auth Failed', color: 'danger' },
      NOT_USED: { label: 'Auth Not Used', color: 'warning' },
      NONE: { label: 'No Auth Records', color: 'secondary' },
    };

    // Get label and color from mapping, otherwise use raw authStatus
    const { label, color } = authStatusMap[authStatus] || {
      label: authStatus,
      color: 'secondary',
    };

    return <Badge color={color}>{label}</Badge>;
  };

  // Create data array for SummaryCard
  const cardData = [
    { key: 'ID', value: webapp_id },
    { key: 'URL', value: url },
    { key: 'OS', value: webapp_os || 'N/A' },
    { key: 'Status', value: is_active ? 'Active' : 'Inactive' },
    { key: 'Owner', value: ownerName },
  ];

  // Filter out any undefined values
  const filteredData = cardData.filter((item) => item.value !== undefined);

  // Create scan status badge if available
  const getScanStatusBadge = () => {
    if (!scanData?.summary?.resultsStatus && !scanData?.status) return null;

    const status = scanData.summary.resultsStatus || scanData.status;

    // Status mapping for labels and colors
    const statusMap: Record<string, { label: string; color: string }> = {
      FINISHED: { label: 'Scan Finished', color: 'success' },
      SUCCESSFUL: { label: 'Scan Successful', color: 'success' },
      RUNNING: { label: 'Scan Running', color: 'info' },
      FAILED: { label: 'Scan Failed', color: 'danger' },
      ERROR: { label: 'Scan Error', color: 'danger' },
      CANCELED: { label: 'Scan Canceled', color: 'warning' },
    };

    // Get label and color from mapping, otherwise format dynamically
    const { label, color } = statusMap[status] || {
      label: `Scan ${String(status).replaceAll('_', ' ').toLowerCase()}`,
      color: 'secondary',
    };

    return (
      <Badge
        className="ml-2"
        color={color}
        style={{ textTransform: 'capitalize' }}
      >
        {label}
      </Badge>
    );
  };

  return (
    <SummaryCard
      heading={
        <div className="d-flex align-items-center gap-2">
          <span>{name || 'Web Application'}</span>
          {getScanStatusBadge()}
        </div>
      }
      data={filteredData}
      description={description}
      icon={
        <div className="d-flex flex-column align-items-center">
          <i
            className="fa fa-globe"
            aria-hidden="true"
            style={{ fontSize: '2.5rem' }}
          ></i>
          <div className="mt-2">{getAuthBadge()}</div>
        </div>
      }
      mode={mode}
    />
  );
};

export default WebAppInfoSummary;
